// Gift variant registered with shared CartHiddenItems patcher (defined in cart.js).
// Registration is done in GiftOrder.connectedCallback below.

// Persists the accordion open state across cart DOM re-renders.
let _giftAccordionWasOpen = false;

// Persists the drawer modal open state across cart DOM re-renders.
let _giftDrawerModalWasOpen = false;

class GiftOrder extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[name="attributes[gift-order]"]');
    this.note = this.querySelector('gift-order-note');

    if (typeof CartHiddenItems !== 'undefined') {
      CartHiddenItems.registerGift(this.variantId);
    }

    // Restore accordion open state after re-render (main-cart).
    const accordion = this.querySelector('[is="accordion-details"]');
    const addonEl = this.closest('.cart__addon');
    if (accordion) {
      if (_giftAccordionWasOpen) {
        accordion.open = true;
        if (addonEl) addonEl.classList.add('active');
      } else {
        _giftAccordionWasOpen = accordion.open;
        if (addonEl) addonEl.classList.toggle('active', accordion.open);
      }
      accordion.addEventListener('toggle', () => {
        _giftAccordionWasOpen = accordion.open;
        if (addonEl) addonEl.classList.toggle('active', accordion.open);
      });
    }

    // Restore drawer modal open state after re-render.
    const drawerModal = this.closest('cart-addon-modal');
    if (drawerModal) {
      if (_giftDrawerModalWasOpen) {
        // Re-open without animation — just remove hidden so it's visible.
        drawerModal.removeAttribute('hidden');
        drawerModal.setAttribute('open', 'immediate');
      }
      new MutationObserver(() => {
        _giftDrawerModalWasOpen = !drawerModal.hasAttribute('hidden');
      }).observe(drawerModal, { attributes: true, attributeFilter: ['hidden'] });
    }

    this.checkbox.addEventListener('change', (event) => {
      if (event.target.checked) {
        this.note.classList.remove('hidden');
        this.addGiftOrder();
      } else {
        this.note.classList.add('hidden');
        this.removeGiftOrder();
      }
    });
  }

  addGiftOrder() {
    this.showLoader();
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    const body = JSON.stringify({
      updates: { [this.variantId]: 1 },
      attributes: { 'gift-order': 'yes', 'gift-order-variant-id': this.variantId },
      sections: sectionsToBundle,
    });
    this.updateCart(body);
  }

  removeGiftOrder() {
    this.showLoader();
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    const body = JSON.stringify({
      updates: { [this.variantId]: 0 },
      attributes: { 'gift-order': '', 'gift-order-note': '', 'gift-order-variant-id': '' },
      sections: sectionsToBundle,
    });
    this.updateCart(body);
  }

  updateCart(body) {
    fetch(FoxTheme.routes.cart_update_url, { ...FoxTheme.utils.fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
      })
      .catch((error) => console.error('Error updating cart:', error));
  }

  showLoader() {
    const loader = this.querySelector('.loader');
    if (loader) loader.classList.add('is-loading');
  }
}
customElements.define('gift-order', GiftOrder);

class GiftOrderNote extends HTMLElement {
  constructor() {
    super();
    this.addEventListener(
      'change',
      FoxTheme.utils.debounce((event) => {
        const body = JSON.stringify({ attributes: { 'gift-order-note': event.target.value } });
        fetch(FoxTheme.routes.cart_update_url, { ...FoxTheme.utils.fetchConfig(), body });
      }, 300)
    );
  }
}
customElements.define('gift-order-note', GiftOrderNote);

class RemoveGiftOrderButton extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').showLoader(this.dataset.index);
      document.querySelector('gift-order').removeGiftOrder();
    });
  }
}
customElements.define('gift-order-remove-item', RemoveGiftOrderButton, { extends: 'a' });

// Monitors cart changes and removes the gift product if no eligible items remain.
const GiftOrderController = {
  _removing: false,

  init() {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  },

  onCartUpdate(event) {
    if (this._removing) return;

    const cart = event.cart;
    if (!cart) return;

    const giftVariantId = cart.attributes?.['gift-order-variant-id']
      ? String(cart.attributes['gift-order-variant-id'])
      : null;
    if (!giftVariantId) return;

    const items = cart.items || [];

    const giftItem = items.find((item) => String(item.variant_id) === giftVariantId);
    if (!giftItem) return;

    // CartHiddenCleanup (cart.js) handles the "all items are hidden" case.
    // Here we only check eligibility: if <gift-order> is missing from the
    // rendered sections, no eligible items remain → remove gift.
    const sections = cart.sections;
    if (!sections) return;
    const renderedHtml = Object.values(sections).join('');
    if (!renderedHtml.includes('<gift-order')) {
      this.removeGiftFromCart(giftItem.key);
    }
  },

  async removeGiftFromCart(giftItemKey) {
    this._removing = true;

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    try {
      const changeState = await fetch(FoxTheme.routes.cart_change_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ id: giftItemKey, quantity: 0, sections: sectionsToBundle }),
      }).then((r) => r.json());

      const attrState = await fetch(FoxTheme.routes.cart_update_url, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({
          attributes: { 'gift-order': '', 'gift-order-note': '', 'gift-order-variant-id': '' },
        }),
      }).then((r) => r.json());

      attrState.sections = changeState.sections;

      this._removing = false;
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: attrState });
    } catch (error) {
      this._removing = false;
      console.error('Error removing gift product:', error);
    }
  },
};

GiftOrderController.init();
